import { IPropertyEditable } from "../../../../base/domain/system/edit";
import {
  IHistoryRecord,
  IPrintBehavior,
  IEditBehavior,
  IQuoteBehavior,
  ITooltipBehavior,
  INaturalTextBehavior,
  ICheckBehavior,
  PatientProfileCheckKinds,
  IObjectNameIdentifier,
  ILinkedObjectSpecifiable,
  IObjectsControllable,
  ILinkedPageObjectSpecifiable,
  ILinkedObjectGroupSpecifiable,
  TranscribeTargetKinds,
} from "../../../../documaker/domain/format/objects";
import { IFormatSpecification } from "../IFormatSpecification";
import { ICheckableObject } from "../objects";
import {
  IInformationKeyHistoriesPropertyEditable,
  IPrintBehaviorPropertyEditable,
  IEditBehaviorPropertyEditable,
  IQuoteBehaviorPropertyEditable,
  ITooltipBehaviorPropertyEditable,
  INaturalTextBehaviorPropertyEditable,
  ICategoryNamePropertyEditable,
  ICheckBehaviorPropertyEditable,
  ITranscribeBehaviorPropertyEditable,
} from "../properties";
import { AbstractRectangleCoordinatableObjectEditor } from "./AbstractRectangleCoordinatableObjectEditor";

export abstract class AbstractCheckableObjectEditor<TObject extends ICheckableObject> extends AbstractRectangleCoordinatableObjectEditor<TObject> implements
IInformationKeyHistoriesPropertyEditable,
IPrintBehaviorPropertyEditable,
IEditBehaviorPropertyEditable,
IQuoteBehaviorPropertyEditable,
ITooltipBehaviorPropertyEditable,
INaturalTextBehaviorPropertyEditable,
ICategoryNamePropertyEditable,
ICheckBehaviorPropertyEditable,
ITranscribeBehaviorPropertyEditable {
  constructor(source: TObject | AbstractCheckableObjectEditor<TObject>, specification?: IFormatSpecification, isNew: boolean = false) {
    super(source, specification, isNew);
    this._id = this.createIdPropertyEditor();
    // check/circleselectの場合はnameは全てHIS情報として扱ってるので名称履歴はinformationKeyHistoriesを記録する.(更新する情報は一緒.UIが別物なので分ける.)
    this._informationKeyHistories = this.createNameHistoriesPropertyEditor();
    this._hisInformationKey = this.createInformationKeyPropertyEditor();
    this._bookmark = this.createBookmarkPropertyEditor();
    this._printBehavior = this.createPropertyEditor(s => s.printBehavior, (s, v) => s.printBehavior = v);
    this._editBehavior = this.createPropertyEditor(s => s.editBehavior, (s, v) => s.editBehavior = v);
    this._quoteBehavior = this.createPropertyEditor(s => s.quoteBehavior, (s, v) => s.quoteBehavior = v);
    this._tooltipBehavior = this.createPropertyEditor(s => s.tooltipBehavior, (s, v) => s.tooltipBehavior = v);
    this._naturalTextBehavior = this.createPropertyEditor(s => s.naturalTextBehavior, (s, v) => s.naturalTextBehavior = v);
    this._categoryName = this.createPropertyEditor(s => s.categoryName, (s, v) => s.categoryName = v);
    this._checkBehavior = this.createPropertyEditor(s => s.checkBehavior, (s, v) => s.checkBehavior = v);
    this._scriptExecuteTriggerBehavior = this.createPropertyEditor(s => s.scriptExecuteTriggerBehavior, (s, v) => s.scriptExecuteTriggerBehavior = v);
    this._transcribeBehavior = this.createTranscribeBehaviorPropertyEditor(s => (0 < this.getSelectableTranscribeTargets().length));  // 既定は選択可能な転記先がある場合のみ編集可に.必要な派生クラスでoverrideする想定.
    this._emptyAlertAllowBehavior = this.createPropertyEditor(s => s.emptyAlertAllowBehavior, (s, v) => s.emptyAlertAllowBehavior = v);
    this._groupAlertAllowBehavior = this.createPropertyEditor(s => s.groupAlertAllowBehavior, (s, v) => s.groupAlertAllowBehavior = v);
  }

  protected _informationKeyHistories: IPropertyEditable<IHistoryRecord<IObjectNameIdentifier>[]>;
  protected _printBehavior: IPropertyEditable<IPrintBehavior>;
  protected _editBehavior: IPropertyEditable<IEditBehavior>;
  protected _quoteBehavior: IPropertyEditable<IQuoteBehavior>;
  protected _tooltipBehavior: IPropertyEditable<ITooltipBehavior>;
  protected _naturalTextBehavior: IPropertyEditable<INaturalTextBehavior>;
  protected _categoryName: IPropertyEditable<string>;
  protected _checkBehavior: IPropertyEditable<ICheckBehavior>;
  protected _scriptExecuteTriggerBehavior: IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>>;
  protected _transcribeBehavior: IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>>;
  protected _emptyAlertAllowBehavior: IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>>;
  protected _groupAlertAllowBehavior: IPropertyEditable<IObjectsControllable<ILinkedObjectGroupSpecifiable>>;

  protected createTranscribeBehaviorPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>> {
    return this.createPropertyEditor(
      s => s.transcribeBehavior,
      (s, v) => s.transcribeBehavior = v,
      isWritable ? isWritable : s => true);
  }

  informationKeyHistories(): IPropertyEditable<IHistoryRecord<IObjectNameIdentifier>[]> {
    return this._informationKeyHistories;
  }

  isHisInformationMixable(): boolean {
    return true;
  }

  printBehavior(): IPropertyEditable<IPrintBehavior> {
    return this._printBehavior;
  }

  editBehavior(): IPropertyEditable<IEditBehavior> {
    return this._editBehavior;
  }

  quoteBehavior(): IPropertyEditable<IQuoteBehavior> {
    return this._quoteBehavior;
  }

  tooltipBehavior(): IPropertyEditable<ITooltipBehavior> {
    return this._tooltipBehavior;
  }

  naturalTextBehavior(): IPropertyEditable<INaturalTextBehavior> {
    return this._naturalTextBehavior;
  }

  categoryName(): IPropertyEditable<string> {
    return this._categoryName;
  }

  checkBehavior(): IPropertyEditable<ICheckBehavior> {
    return this._checkBehavior;
  }

  scriptExecuteTriggerBehavior(): IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>> {
    return this._scriptExecuteTriggerBehavior;
  }

  transcribeBehavior(): IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>> {
    return this._transcribeBehavior;
  }

  abstract getSelectableTranscribeTargets(): TranscribeTargetKinds[];

  emptyAlertAllowBehavior(): IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>> {
    return this._emptyAlertAllowBehavior;
  }

  groupAlertAllowBehavior(): IPropertyEditable<IObjectsControllable<ILinkedObjectGroupSpecifiable>> {
    return this._groupAlertAllowBehavior;
  }

  getWarningName(): string {
    return this.getHisInformationName();
  }

  getWarningSettingName(): string {
    return this._hisInformationKey.getValue();
  }

  isSettableWarningName(): boolean {
    return true;
  }

  abstract isPatientProfileCheckKindSelectable(): boolean;
  abstract getSelectablePatientProfileCheckKinds(): PatientProfileCheckKinds[];
  abstract isGroupScopeSelectable(): boolean;

  getIdentifiableName(): string {
    return this.getIdentifiableHisName() + "_" + this.categoryName().getValue();
  }

  toLinkedObjectIdentifier(): ILinkedObjectSpecifiable {
    return {
      type: this.objectTypeName().getValue(),
      id: this.id().getValue(),
      key: this.hisInformationKey().getValue(),
      subKey: this.categoryName().getValue(),
    };
  }
}
